/**
 * User roles in Masti Manch platform
 */
export enum UserRole {
  STUDENT = 'Student',
  PARENT = 'Parent',
  TEACHER = 'Teacher',
  ASSESSMENT_TEAM = 'AssessmentTeam',
  VIDEO_TEAM = 'VideoTeam',
  ADMIN = 'Admin',
  GUEST = 'Guest'
}

/**
 * Portal types for redirect logic
 */
export enum PortalType {
  LANDING = 'mastimanch.com',
  STUDENT = 'study.mastimanch.com',
  PARENT = 'parent.mastimanch.com',
  TEACHER = 'teams.mastimanch.com',
  ADMIN = 'admin.mastimanch.com'
}

/**
 * User registration attributes
 */
export interface UserAttributes {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  board?: string;
  standard?: string;
  school?: string;
  state?: string;
  phoneNumber?: string;
  address?: string;
  parentId?: string;
  studentId?: string;
  subscriptionStatus?: string;
  trialEndDate?: string;
}

/**
 * Cognito user session
 */
export interface AuthSession {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Chat feature type definitions
// ─────────────────────────────────────────────────────────────────────────────

export interface ChatSession {
  id: string;
  subjectId: number;
  standardId: number;
  subjectName?: string;
  lastMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChatMessage {
  messageId: string;
  sessionId: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}


export interface Topic {
  videoId: number;
  audioId: number;
  topicName: string;
  bookId?: number;
  topicDescription: string;
  thumbnailUrl: string | null;
  duration: number;
  publishedAt: string;
}

export interface Chapter {
  chapterId: number;
  chapterNumber: number;
  chapterName: string;
  subjectCode: string;
  topics: Topic[];
}

export interface BookInfo {
  id?: number;
  name: string;
  code: string;
  description?: string;
  medium: string;
  subjectName?: string;
  subjectCode?: string;
  publisher?: string;
  subjectId?: number;
}

export interface PagedMessages {
  messages: ChatMessage[];
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface SendMessageRequest {
  SubjectId: number;
  StandardId: number;
  Message: string;
}

export interface SendMessageResponse {
  sessionId: string;
  userMessage: ChatMessage;
  assistantMessage: ChatMessage;
}


/**
 * Current authenticated user
 */
export interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  attributes: Record<string, string>;
  isEmailVerified: boolean;
  session?: AuthSession | null;
}

/**
 * Auth state for React context
 */
export interface AuthState {
  user: CurrentUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

/**
 * Login credentials
 */
export interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * Registration data
 */
export interface RegistrationData extends LoginCredentials {
  attributes: Partial<UserAttributes>;
}

/**
 * Password reset data
 */
export interface PasswordResetData {
  email: string;
  code: string;
  newPassword: string;
}

/**
 * Email verification data
 */
export interface EmailVerificationData {
  email: string;
  code: string;
}

/**
 * Force password change data
 */
export interface ForcePasswordChangeData {
  user: any; // Cognito user object
  newPassword: string;
}

/**
 * Portal configuration
 */
export interface PortalConfig {
  type: PortalType;
  allowedRoles: UserRole[];
  loginPath: string;
  dashboardPath: string;
}

/**
 * Auth configuration for AWS Amplify
 */
export interface AuthConfig {
  region: string;
  userPoolId: string;
  userPoolWebClientId: string;
  identityPoolId?: string;
  oauth?: {
    domain: string;
    scope: string[];
    redirectSignIn: string;
    redirectSignOut: string;
    responseType: string;
  };
}

/**
 * SSO Redirect options
 */
export interface SSORedirectOptions {
  currentPortal: PortalType;
  userRole: UserRole;
  redirectToLogin?: boolean;
}

/**
 * Error types
 */
export interface AuthError {
  code: string;
  message: string;
  name: string;
}

/**
 * Helper type for role-based access
 */
export type AllowedRoles = UserRole[] | 'all' | 'authenticated';

/**
 * Route protection options
 */
export interface RouteProtectionOptions {
  allowedRoles: AllowedRoles;
  redirectTo?: string;
  fallbackMessage?: string;
}
